import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageSquare, Plus, X, Search, ChevronLeft } from "lucide-react";
import { useLang } from "@/context/LangContext";

const ROLE_BADGES = {
  admin: { bg: "bg-wine-100 text-wine-700", label: "Admin" },
  member: { bg: "bg-emerald-100 text-emerald-700", label: "Membre" },
  press: { bg: "bg-amber-100 text-amber-700", label: "Presse" },
  journalist: { bg: "bg-amber-100 text-amber-700", label: "Presse" },
};

export default function MemberMessages() {
  const { t } = useLang();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const [composing, setComposing] = useState(false);
  const [newMsg, setNewMsg] = useState({ to_user_id: "", to_label: "", subject: "", body: "" });
  const [recipients, setRecipients] = useState([]);
  const [recipientQ, setRecipientQ] = useState("");

  const loadThreads = () => api.get("/messages/threads").then((r) => setThreads(r.data));
  const loadActive = (tid) => api.get(`/messages/threads/${tid}`).then((r) => { setActive(r.data); loadThreads(); });

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (!composing) return;
    const tmo = setTimeout(() => api.get("/messages/users", { params: { q: recipientQ } }).then((r) => setRecipients(r.data)).catch(() => {}), 300);
    return () => clearTimeout(tmo);
  }, [recipientQ, composing]);

  const reply = async () => {
    if (!draft.trim() || !active) return;
    try {
      await api.post(`/messages/threads/${active.id}/reply`, { body: draft });
      setDraft("");
      loadActive(active.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendNew = async () => {
    if (!newMsg.to_user_id || !newMsg.subject.trim() || !newMsg.body.trim()) { toast.error("Tous les champs sont requis"); return; }
    try {
      const r = await api.post("/messages/new", { to_user_id: newMsg.to_user_id, subject: newMsg.subject, body: newMsg.body });
      toast.success("Message envoyé");
      setComposing(false);
      setNewMsg({ to_user_id: "", to_label: "", subject: "", body: "" });
      loadThreads();
      loadActive(r.data.thread_id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <PortalLayout sidebar="member">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Messages</h1>
          <p className="mt-1 text-neutral-600">{t("portal_membermessages_01")}</p>
        </div>
        <button onClick={() => setComposing(true)} className="inline-flex items-center gap-2 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="msg-new"><Plus className="h-3.5 w-3.5" />{t("portal_membermessages_90")}</button>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[320px_1fr]">
        {/* Thread list */}
        <div className="space-y-2">
          {threads.length === 0 && <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/40 p-8 text-center text-sm text-neutral-500" data-testid="msg-empty">Aucune conversation.</div>}
          {threads.map((t) => {
            const recap = t.participants?.[0];
            const badge = recap ? ROLE_BADGES[recap.role] : null;
            return (
              <button key={t.id} onClick={() => loadActive(t.id)} className={`block w-full rounded-2xl border p-4 text-left transition-all ${active?.id === t.id ? "border-wine-500 bg-wine-50" : t.unread > 0 ? "border-wine-200 bg-wine-50/40" : "border-neutral-100 bg-white hover:border-wine-200"}`} data-testid={`thread-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-neutral-900 truncate">{recap?.name || "Conversation"}</div>
                  {t.unread > 0 && <span className="rounded-full bg-wine-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{t.unread}</span>}
                </div>
                {badge && <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[9px] font-bold ${badge.bg}`}>{badge.label}</span>}
                <div className="mt-1 text-xs font-semibold text-neutral-700">{t.subject}</div>
                <div className="mt-0.5 truncate text-xs text-neutral-500">{t.last_snippet}</div>
              </button>
            );
          })}
        </div>

        {/* Active thread */}
        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          {!active && <div className="grid h-80 place-items-center text-sm text-neutral-400"><div className="text-center"><MessageSquare className="mx-auto h-8 w-8 text-neutral-300" /><div className="mt-2">{t("portal_membermessages_02")}</div></div></div>}
          {active && (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-neutral-100 pb-3">
                <div>
                  <button onClick={() => setActive(null)} className="mb-1 inline-flex items-center gap-1 text-[10px] text-neutral-500 hover:text-wine-700 lg:hidden"><ChevronLeft className="h-3 w-3" />Retour</button>
                  <div className="font-bold text-neutral-900">{active.subject}</div>
                </div>
              </div>
              <div className="mt-4 max-h-[480px] space-y-3 overflow-y-auto">
                {active.messages.map((m) => {
                  const isMine = m.sender?.id === undefined || m.from_user_id === active.participants?.[0]; // crude check OK for layout
                  return (
                    <div key={m.id} className="flex gap-2" data-testid={`msg-${m.id}`}>
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-wine-100 text-xs font-bold text-wine-700">{(m.sender?.name || "?")[0]}</div>
                      <div className="flex-1">
                        <div className="text-xs"><span className="font-semibold text-neutral-900">{m.sender?.name || "—"}</span> <span className="text-neutral-400">· {new Date(m.created_at).toLocaleString("fr")}</span></div>
                        <div className="mt-1 whitespace-pre-wrap rounded-2xl bg-neutral-50 px-3 py-2 text-sm text-neutral-800">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 border-t border-neutral-100 pt-3">
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("portal_membermessages_91")} rows={3} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="msg-reply-input" />
                <div className="mt-2 flex justify-end">
                  <button onClick={reply} disabled={!draft.trim()} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-40" data-testid="msg-reply-send"><Send className="h-3 w-3" />{t("portal_membermessages_92")}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {composing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="msg-compose-modal">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between"><h3 className="text-xl font-bold text-navy">{t("portal_membermessages_90")}</h3><button onClick={() => setComposing(false)}><X className="h-5 w-5 text-neutral-400" /></button></div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-bold text-neutral-700">Destinataire</label>
                {newMsg.to_user_id ? (
                  <div className="mt-1 inline-flex items-center gap-2 rounded-full bg-wine-50 px-3 py-1 text-xs font-bold text-wine-700">{newMsg.to_label}<button onClick={() => setNewMsg({ ...newMsg, to_user_id: "", to_label: "" })}><X className="h-3 w-3" /></button></div>
                ) : (
                  <>
                    <div className="relative mt-1"><Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" /><input value={recipientQ} onChange={(e) => setRecipientQ(e.target.value)} placeholder={t("portal_membermessages_93")} className="w-full rounded-xl border border-neutral-200 pl-9 pr-3 py-2 text-sm" data-testid="recipient-search" /></div>
                    <div className="mt-1 max-h-44 space-y-1 overflow-y-auto">
                      {recipients.slice(0, 8).map((u) => (
                        <button key={u.id} onClick={() => setNewMsg({ ...newMsg, to_user_id: u.id, to_label: `${u.name} (${u.role})` })} className="block w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-wine-50" data-testid={`recipient-${u.id}`}>
                          <div className="font-semibold text-neutral-900">{u.name}</div>
                          <div className="text-[10px] text-neutral-500">{u.email} · {u.role}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <input value={newMsg.subject} onChange={(e) => setNewMsg({ ...newMsg, subject: e.target.value })} placeholder="Objet" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="msg-subject" />
              <textarea value={newMsg.body} onChange={(e) => setNewMsg({ ...newMsg, body: e.target.value })} rows={5} placeholder={t("portal_membermessages_94")} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="msg-body" />
              <button onClick={sendNew} className="w-full rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="msg-send">{t("portal_membermessages_92")}</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
